import React, { useState, useEffect } from 'react'
import CategorySearchComboBox from './CategorySearchComboBox.jsx'
import { authenticatedFetch, getAuthHeaders } from './utils/auth.js'

function CategorySuggestionModal({ 
  isOpen, 
  onClose, 
  item, 
  onSuggestionSubmitted,
  skipAutoCheck = false // If true, skip the backend check and always show the modal
}) {
  const [suggestedCategory, setSuggestedCategory] = useState('')
  const [availableCategories, setAvailableCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('Checking if suggestion is needed...')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (isOpen && item) {
      // Reset state when modal opens
      setLoading(true)
      setError('')
      setSuccess(false)
      setSuggestedCategory('')
      setAvailableCategories([])
      
      // Check if suggestion should be shown (skip if manually triggered)
      if (skipAutoCheck) {
        // Load categories directly without checking eligibility
        setLoadingMessage('Loading categories...')
        loadAvailableCategories()
      } else {
        setLoadingMessage('Checking if suggestion is needed...')
        checkShouldSuggest()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, item, skipAutoCheck])

  // Handle escape key
  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  async function loadAvailableCategories() {
    try {
      // Use a timeout to prevent hanging
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout
      
      const response = await authenticatedFetch('/api/category-suggestions/available-categories', {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
        },
        signal: controller.signal
      })
      
      clearTimeout(timeoutId)
      
      if (response.ok) {
        const data = await response.json()
        setAvailableCategories(data.categories || [])
        setLoading(false)
      } else {
        const errorText = await response.text()
        console.error('Failed to load categories:', response.status, errorText)
        throw new Error(`Failed to load categories: ${response.status}`)
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.error('Request timeout loading categories')
        setError('Request timed out. Please try again.')
      } else {
        console.error('Error loading categories:', err)
        setError('Failed to load categories. You can still close this modal.')
      }
      setLoading(false)
    }
  }

  async function checkShouldSuggest() {
    if (!item) return

    try {
      const response = await fetch(
        `/api/category-suggestions/should-suggest?` +
        `item_name=${encodeURIComponent(item.item)}&` +
        `category=${encodeURIComponent(item.category || '')}&` +
        `confidence=${item.confidence || 0}&` +
        `method=${encodeURIComponent(item.method || '')}`
      )
      
      const data = await response.json()
      
      if (data.should_suggest) {
        setAvailableCategories(data.available_categories)
        setLoading(false)
      } else {
        // Don't show modal if suggestion shouldn't be triggered (only for auto-triggered modals)
        onClose()
      }
    } catch (err) {
      console.error('Error checking suggestion eligibility:', err)
      setError('Failed to check suggestion eligibility. You can still close this modal.')
      setLoading(false)
      // Still show the modal with error, don't auto-close
    }
  }

  async function submitSuggestion() {
    if (!suggestedCategory || !item) return

    setSubmitting(true)
    setError('')

    try {
      const response = await authenticatedFetch('/api/category-suggestions/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          item_name: item.item,
          current_category: item.category,
          suggested_category: suggestedCategory,
          confidence_score: item.confidence,
          classification_method: item.method
        })
      })

      if (response.ok) {
        setSuccess(true)
        setTimeout(() => {
          onSuggestionSubmitted && onSuggestionSubmitted(item, suggestedCategory)
          onClose()
        }, 1500)
      } else {
        const errorData = await response.json()
        setError(errorData.detail || 'Failed to submit suggestion')
      }
    } catch (err) {
      console.error('Error submitting suggestion:', err)
      setError('Failed to submit suggestion')
    } finally {
      setSubmitting(false)
    }
  }

  function handleClose() {
    setSuggestedCategory('')
    setError('')
    setSuccess(false)
    setLoading(false)
    onClose()
  }

  if (!isOpen || !item) return null

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001,
        padding: '16px',
        boxSizing: 'border-box'
      }}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="suggestion-title"
      aria-describedby="suggestion-description"
    >
      <div 
        className="card"
        style={{
          maxWidth: 'min(512px, calc(100vw - 32px))',
          width: '100%',
          maxHeight: 'min(90vh, calc(100vh - 32px))',
          overflowX: 'hidden',
          overflowY: 'auto',
          position: 'relative',
          padding: '24px',
          boxSizing: 'border-box',
          minWidth: 0,
          overflowWrap: 'break-word'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="ghost"
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            padding: '4px',
            minWidth: 'auto',
            minHeight: 'auto',
            width: '32px',
            height: '32px',
            fontSize: '20px',
            color: 'var(--text-secondary)'
          }}
          title="Close"
          aria-label="Close suggestion modal"
        >
          ×
        </button>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              <i className="fas fa-spinner fa-spin" aria-hidden="true" style={{ marginRight: '8px' }}></i>
              {loadingMessage}
            </div>
          </div>
        ) : success ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ 
              fontSize: '16px', 
              color: 'var(--color-green-600)',
              marginBottom: '12px',
              fontWeight: '600'
            }}>
              <i className="fas fa-check-circle" aria-hidden="true" style={{ marginRight: '8px' }}></i>
              Suggestion submitted successfully!
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Thank you for helping improve our categorization.
            </div>
            <button
              onClick={handleClose}
              className="secondary"
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                minHeight: '44px'
              }}
              aria-label="Close suggestion modal"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h2 id="suggestion-title" style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '20px',
              color: 'var(--text-primary)',
              margin: '0 0 20px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-lightbulb" aria-hidden="true" style={{ color: 'var(--color-orange-600)' }}></i>
              <span>Suggest a Better Category</span>
            </h2>

            <div id="suggestion-description" className="card" style={{
              fontSize: '14px',
              marginBottom: '20px',
              padding: '16px',
              background: 'var(--bg-subtle)'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong style={{ color: 'var(--text-primary)' }}>Item:</strong>{' '}
                <span style={{ color: 'var(--text-primary)' }}>{item.item}</span>
              </div>
              <div>
                <strong style={{ color: 'var(--text-primary)' }}>Current Category:</strong>{' '}
                {item.category ? (
                  <span className="badge badge-category">{item.category}</span>
                ) : (
                  <span style={{ color: 'var(--text-tertiary)' }}>Unknown</span>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <CategorySearchComboBox
                id="category-suggestion-search"
                label="Suggested Category:"
                availableCategories={availableCategories}
                value={suggestedCategory}
                onChange={setSuggestedCategory}
                placeholder="Search or type category name..."
                size="medium"
                maxHeight={200}
              />
            </div>

            {error && (
              <div className="card" style={{
                fontSize: '14px',
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: 'var(--color-error-bg)',
                borderColor: 'var(--color-error-border)',
                borderWidth: '1px',
                color: 'var(--color-error)'
              }}>
                <i className="fas fa-exclamation-circle" aria-hidden="true" style={{ marginRight: '8px' }}></i>
                {error}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleClose}
                disabled={submitting}
                className="secondary"
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  minHeight: '44px'
                }}
                aria-label="Cancel suggestion"
              >
                Cancel
              </button>
              <button
                onClick={submitSuggestion}
                disabled={!suggestedCategory || submitting}
                className={suggestedCategory && !submitting ? 'primary' : ''}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px',
                  minHeight: '44px'
                }}
                aria-label={submitting ? 'Submitting suggestion' : 'Submit suggestion'}
              >
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" style={{ marginRight: '8px' }}></i>
                    Submitting...
                  </>
                ) : (
                  'Submit Suggestion'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CategorySuggestionModal
